/**
 * Help Center content — Part 4: Docs & Compliance, Voyages, Setting up Operations,
 * Customising Your Checklists.
 * Imported by helpContent/index.js; do not import directly.
 */

// ─── 9. DOCS & COMPLIANCE ────────────────────────────────────────────────────

export const docsCompliance = {
  id: 'docs-compliance',
  title: '9. Docs & Compliance',
  summary: 'Track your vessel certificates and SMS documents by expiry date, with links to your cloud files.',
  keywords: ['docs', 'compliance', 'certificate', 'sms', 'document', 'expiry', 'link', 'google drive', 'sharepoint', 'onedrive', 'dropbox', 'icloud', 'valid', 'expiring', 'overdue'],
  steps: [
    {
      text: 'Docs & Compliance is a compliance register that tracks your vessel\'s certificates, SMS documents and risk assessments and — most importantly — their expiry / review dates, with a colour-coded status at a glance. FleetSkipper does not upload or store the document files themselves — you keep your files in your own cloud storage and give FleetSkipper the link to each one. It\'s a tracker, not a filing cabinet. Reach it from left menu → Docs & Compliance.',
      image: 'docs-02-landing.png',
    },
    {
      text: 'Click + Add Item (or Add First Item). The Add Compliance Item dialog has: Document Name, Category (Certificate / SMS Document / Risk Assessment), Expiry / Review Date ("Leave blank if no expiry applies"), Responsible Person, Document Link (a URL — "Link to Google Drive, SharePoint, or other document storage"), and Notes. There is no file-upload box — you paste a link.',
      image: 'docs-03-add-form.png',
    },
    {
      text: 'Click Add Item. The item appears in the register with a coloured Status chip and the Compliance Summary counts update.',
      image: 'docs-05-saved.png',
    },
    {
      text: 'Status colours: green Valid (future date), amber Expiring (date approaching), red Overdue (date passed), grey Not Set (no date).',
      image: 'docs-06-all-states.png',
    },
    {
      text: 'Open a linked document: click the external-link icon in the Link column — the document opens in a new browser tab at your cloud storage. Items with no link show a dash.',
      image: null,
    },
    {
      text: 'Filter with the tabs (Certificates / SMS Documents / Risk Assessments / All) and the Search items... box.',
      image: 'docs-07-tab-certificates.png',
    },
    {
      text: 'Edit an item via the pencil icon; Delete via the trash icon.',
      image: 'docs-10-edit-modal.png',
    },
    {
      text: 'Set Up Documents Folder (Quick Actions) lets you register one master link to your vessel\'s cloud documents folder — pick a Storage Provider (SharePoint / Google Drive / OneDrive / Dropbox / iCloud Drive / Other) and paste the Folder URL, then Save.',
      image: 'docs-09-folder-setup.png',
    },
  ],
};

// ─── 10. VOYAGES ─────────────────────────────────────────────────────────────

export const voyages = {
  id: 'voyages',
  title: '10. Voyages',
  summary: 'Start a voyage, work through the safety gate, log positions and events, and record your arrival.',
  keywords: ['voyage', 'let go', 'departure', 'arrival', 'master', 'crew', 'passengers', 'pob', 'pre-departure', 'weather', 'fuel', 'log', 'position', 'underway'],
  steps: [
    {
      text: 'A voyage in FleetSkipper is one trip your boat makes — from letting go at one place to arriving at the next. Starting a voyage has a built-in safety gate: before it lets you depart ("LET GO"), the app checks you\'re properly crewed — a qualified Master aboard, minimum manning (at least two crew), and someone with a radio certificate. You reach voyages from left menu → Operations.',
      image: null,
    },
    {
      text: 'From the left menu, tap Operations. You land on the Voyage tab. You\'ll see (if a trip is running) an Active Voyage card, a Voyage Log panel, tiles for Daily Checks and Start Voyage, and a Quick Actions box on the right with Voyage History and Settings.',
      image: 'cvoy-03-operations-landing.png',
    },
    {
      text: 'To begin a new trip, tap the Start Voyage tile. This opens the Start Voyage form where you set From and To (your ports), the Master, Crew and Passengers aboard (which the app totals as POB — persons on board), the conditions at departure (wind, sea state, visibility) and your departure fuel. You then work through the Pre-Departure Checks before you can let go.',
      image: '04-start-voyage-form.png',
    },
    {
      text: 'If a Master needs a certificate, you can add it (Certificate of Competency) from within this form; a qualified Master is then accepted.',
      image: 'voyage-11-master-cert.png',
    },
    {
      text: 'Set the departure fuel and weather, and tick the pre-departure checks. When everything\'s satisfied, LET GO turns green — tap it, then Confirm and Let Go. Your voyage is now Underway with a running timer. Tip: if LET GO stays greyed out, the app tells you why — usually a missing Master certificate, too few crew, or no radio operator. Fix the flagged item and it clears.',
      image: 'voyage-12-letgo-enabled.png',
    },
    {
      text: 'Once a voyage is running, tapping the Active Voyage card opens the live voyage log. Along the top are filter tabs (All · Position · Weather · Fuel · Notes · Comms · Checks · Milestones), the header shows the route, POB and elapsed time. A toolbar lets you quick-log Position, Weather, Fuel, Compass, Note, Operation and finally Arrive to end the trip.',
      image: 'cvoy-13-active-voyage-detail.png',
    },
  ],
};

// ─── 11. SETTING UP OPERATIONS ───────────────────────────────────────────────

export const settingUpOperations = {
  id: 'setting-up-operations',
  title: '11. Setting up Operations',
  summary: 'Configure your ports, operation types, voyage plans, purpose options, fuel capacity and voyage number format.',
  keywords: ['operations', 'voyage settings', 'ports', 'locations', 'operation types', 'voyage plan', 'crew transfer', 'towing', 'template', 'fuel', 'voyage number', 'logbook', 'customise'],
  steps: [
    {
      text: 'Almost all of the set-up for voyages happens in one place: the Voyage Settings panel. Open Operations from the left menu (you land on the Voyage tab), then on the right, in the Quick Actions box, tap Settings. The Voyage Settings panel opens — "Configure voyage defaults and options." Note: the app-level Settings at the bottom of the left menu is a different thing — that\'s the 8-step Vessel Settings wizard. The Operations set-up is the Settings button inside the Operations page.',
      image: 'cops-02-operations-landing.png',
    },
    {
      text: 'A. Saved Ports / Locations — your regular ports, once: Under Saved Ports / Locations, type a port or place into the box.',
      image: 'cops-03-add-port-typed.png',
    },
    {
      text: 'Tap the + button to the right of the box. Your port drops into the list below with a bin icon next to it. From now on, that port is one tap away whenever you start a voyage.',
      image: 'cops-04-port-added.png',
    },
    {
      text: 'B. Operation Types — the jobs your vessel actually does: You\'ll see Phase Checks (Pre-Departure / Pre-Arrival / Post-Arrival, each marked Required) and Mid-Voyage Operations (Crew Transfer, Pilot Transfer, Standby, Towing, Diving Support, Anchoring, Restricted Visibility, Daily Checks (Mid-Voyage)). Use the toggle on the left of each mid-voyage operation to turn it on or off.',
      image: 'cops-09-settings-mid.png',
    },
    {
      text: 'To edit an operation, tap the pencil icon on its row. This opens the Edit Operation Template editor where you can rename it, add or remove Data Fields, and edit Pre-Operation Checks.',
      image: 'cops-05-edit-operation.png',
    },
    {
      text: 'Don\'t want to build operations from scratch? Tap Browse Templates for ready-made operations — Personnel Transfer (3 fields · 6 checks), Pilot Transfer, Towing Operations, Anchoring, Standby, Diving Support, Restricted Visibility.',
      image: 'cops-06-browse-templates.png',
    },
    {
      text: 'C. Voyage Plans — recipes for your regular runs: In Voyage Settings, scroll to the Voyage Plans section. Tap + Create your first plan.',
      image: 'cvoy-08-voyage-plans-section.png',
    },
    {
      text: 'Give the plan a name (e.g. Larne → Belfast (regular run)) and optionally set a Default From, Default To and Default purpose — these pre-fill the Start Voyage form when a Master picks this plan.',
      image: 'cvoy-09-plan-editor-empty.png',
    },
    {
      text: 'Build the plan\'s Items: tap Add Waypoint to add a point on the route (label + optional instruction note), or Add Operation to slot a planned operation into the trip.',
      image: 'cvoy-10-plan-editor-filled.png',
    },
    {
      text: 'Each item shows up/down arrows to reorder and a bin to remove. Plans save automatically as you edit — no Save Settings click needed. From then on, when a Master starts a voyage they can pick this plan and its ports, purpose, waypoints and planned operations come across ready-made.',
      image: 'cvoy-11-plan-with-items.png',
    },
    {
      text: 'D. Voyage Purpose Options and F. Voyage Number Format: Switch on/off voyage purposes (Commercial Operations, Training, Maintenance/Trials, Transit, Emergency Response) and add your own. Set a Prefix (e.g. WB-2024-) and Start From number — a live Preview shows your next voyage number.',
      image: 'cvoy-12-voyage-number-fuel.png',
    },
    {
      text: 'E. Fuel Capacity: type your vessel\'s tank capacity in litres for fuel tracking calculations. When finished, tap Save Settings at the bottom.',
      image: 'cops-11-settings-verybottom.png',
    },
    {
      text: 'The Logbook: Back on the Operations page, open the Logbook tab. It opens on Today, with arrows to move between days. Most log entries appear on their own — when you raise a defect, run a drill, complete a daily check or record an incident anywhere in the app, a line is written to the log for you.',
      image: 'cops-07-logbook.png',
    },
    {
      text: 'To add something by hand, tap + Add Entry. Pick a Category (General, Visitor on board, Port State inspection, Notice received, Weather event, Other), type Entry Content, add optional Additional Notes, then tap Add Entry.',
      image: 'cops-12-add-log-entry.png',
    },
  ],
};

// ─── 12. CUSTOMISING YOUR CHECKLISTS ─────────────────────────────────────────

export const customisingChecklists = {
  id: 'customising-checklists',
  title: '12. Customising Your Checklists',
  summary: 'Add, reword and remove checklist items, create brand-new checklists, and control which categories the crew see.',
  keywords: ['checklist', 'custom', 'create', 'edit', 'reword', 'remove', 'item', 'category', 'visibility', 'hide', 'daily checks', 'operational', 'emergency', 'gear icon', 'settings', 'audit trail', 'core items'],
  steps: [
    {
      text: 'The checklists that ship with FleetSkipper (daily checks, emergency procedures, navigation and operational) are a starting point, not a straitjacket. You can reword items into your own language, add the checks your procedures require, remove ones that don\'t apply, and add whole new checklists. Note: nothing you do rewrites history — a banner on every editing screen reads "Changes here only affect NEW operations. Existing records keep their original checklist items for audit purposes. All modifications are logged."',
      image: 'cchk-00-checklist-tab.png',
    },
    {
      text: 'Get to the customising screen: Open Operations (left menu), then click the Checklists tab. Click the small gear icon (tooltip: "Checklist Settings"), top-right of the checklist area. This opens Checklist Management.',
      image: 'cchk-01-checklist-settings.png',
    },
    {
      text: 'Checklist Management holds everything: a Category Visibility panel at the top, and a Custom Checklists panel lower down with Create New Checklist and Import Checklist from SMS Document (PRO).',
      image: null,
    },
    {
      text: 'Create a brand-new checklist from scratch: In the Custom Checklists panel, click Create New Checklist. The form opens.',
      image: 'cchk-02-create-new-checklist.png',
    },
    {
      text: 'Fill in Checklist Details: Name (e.g. "MV Demo – Passenger Transfer Brief"), Description, Category, and Reference (Optional). Set Options: Can be linked to active voyage / Auto-log completion to Main Logbook / Requires signature on completion.',
      image: 'cchk-03-create-filled.png',
    },
    {
      text: 'Under Checklist Items, type your first item into the "Check item text…" box and optionally add a guidance note underneath it. Click Add Item to add another row and repeat. Click Create Checklist — your new checklist is saved.',
      image: null,
    },
    {
      text: 'Your new checklist appears to the crew straight away inside the category you chose on the ordinary Operations → Checklists tab.',
      image: 'cchk-13-runner-operational.png',
    },
    {
      text: 'Edit an existing checklist — add, reword and remove items: On Checklist Management, click a category card (e.g. Operational). You get a list of that category\'s checklists, each with an Edit button. Click Edit on the checklist you want to change.',
      image: 'cchk-04-edit-items-view.png',
    },
    {
      text: 'Add an item: click Add New Check Item, type the wording into "Check Item Text", optionally pick a Category tag, then click Add Item.',
      image: 'cchk-05-add-item-form.png',
    },
    {
      text: 'Edit an item\'s wording: click the pencil icon next to the item, change the text, then click Update Item.',
      image: 'cchk-08-edit-item-text.png',
    },
    {
      text: 'Remove an item: click the bin icon. A confirmation appears — "Remove this check item?" — with an optional "Reason for removal" box (good practice for your audit trail). Click Remove Item to confirm.',
      image: 'cchk-10-remove-item.png',
    },
    {
      text: 'Customise the Daily Checks: On Checklist Management, click the Daily Checks card to open Daily Checks Configuration. Click into the Daily Checks group to see all the individual checks. Each has pencil (Edit) and bin (Remove) icons, plus Add Check Item and Add Custom Category buttons at the bottom. Changes are saved automatically.',
      image: 'cchk-16-daily-checks-mgmt.png',
    },
    {
      text: 'Choose which categories the crew see: On Checklist Management, the Category Visibility panel lists Emergency · Navigation · Operational · Daily Checks · Periodic. Each has a small eye icon — click it to hide or show a category. The Emergency category shows a lock "Protected — cannot be hidden" — safety-critical checklists stay visible no matter what.',
      image: 'cchk-15-category-visibility.png',
    },
    {
      text: 'Leave the settings screen and go back to the ordinary Operations → Checklists tab. Open the category you filed your custom checklist under — it runs exactly like a built-in one: items with tick boxes, a General Notes box, and a green Complete Checklist button.',
      image: 'cchk-14-crew-runs-custom.png',
    },
  ],
};
